import React from 'react';
import clsx from 'clsx';
import { CheckIcon, ChevronUpDownIcon, SwatchIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions
} from '@oysterjs/ui/dialog';
import { Badge } from '@oysterjs/ui/badge';
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownItem,
  DropdownLabel,
  DropdownMenu
} from '@oysterjs/ui/dropdown';
import { messageFromError } from '@oysterjs/core/graphql/errors';

import type { AgencyDesignChoices } from '~/api/data/designs/queries';
import type { DataResult } from '~/api/data/result';
import {
  markCaraWorkspaceArtifactFinal,
  setCaraWorkspaceArtifactDesign,
  unmarkCaraWorkspaceArtifactFinal
} from '~/api/data/workspaces/mutations';
import type { CaraWorkspaceFieldsFragment } from '~/api/graphql';
import type { DocumentDesign } from '~/components/workspaces/lib/document-design';
import type { SaveStatus } from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';
import {
  DOCUMENT_PROVENANCE_CLASSES,
  DocumentProvenanceLabel
} from './artifact-focus-html-document-provenance';
import type { VersionAction } from './artifact-focus-html-document-version-picker';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/**
 * The design a document version renders with and whether its look is frozen. Choosing another
 * design makes a new version from the latest one, which the panel moves to once the workspace
 * carries it, so only the latest version offers the choice.
 */
export const DocumentDesignControls = ({
  artifact,
  design,
  choices,
  onDesignsOpen,
  editable,
  latest,
  saveStatus,
  updating = false,
  onVersionChange,
  onArtifactSaved,
  versionPicker,
  children
}: {
  artifact: Artifact;
  design: DocumentDesign;
  choices: AgencyDesignChoices | null;
  /** Asks for the designs again as the Design menu opens, since they change in Settings. */
  onDesignsOpen?: () => void;
  editable: boolean;
  latest: boolean;
  /** The editor's autosave, which a design switch or a mark must not run ahead of. */
  saveStatus: SaveStatus;
  /** A Design refit is replacing this version, so nothing may switch or freeze it meanwhile. */
  updating?: boolean;
  onVersionChange: (artifact: Artifact) => void;
  onArtifactSaved: (artifact: Artifact) => void;
  /** The version picker, handed Mark as final or Unmark final for the version shown. */
  versionPicker: (action?: VersionAction) => React.ReactNode;
  children?: React.ReactNode;
}) => {
  const [pending, setPending] = React.useState<'design' | 'mark' | 'unmark' | null>(null);
  const busy = pending !== null || updating;
  const [confirmFinal, setConfirmFinal] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    setError(null);
    setConfirmFinal(false);
  }, [artifact.id, artifact.version]);

  const final = artifact.document?.final;

  // A switch copies the server's latest into a new version and remounts the editor, which drops
  // a save still pending; a mark makes that save land on a final version and fail.
  const unsaved =
    saveStatus === 'saving'
      ? 'Wait for your edits to save'
      : saveStatus === 'error'
        ? "Your last edit didn't save"
        : null;

  const run = async <T,>(
    action: NonNullable<typeof pending>,
    change: () => Promise<DataResult<T>>,
    savedOf: (data: NonNullable<T>) => Artifact | undefined,
    fallback: string
  ) => {
    setPending(action);
    setError(null);
    try {
      const { data, error: failure } = await change();
      const saved = data ? savedOf(data) : undefined;
      if (failure || !saved) {
        setError(messageFromError(failure, fallback));
        return undefined;
      }
      return saved;
    } catch (failure) {
      setError(messageFromError(failure, fallback));
      return undefined;
    } finally {
      setPending(null);
    }
  };

  const choose = async (designId: string | null) => {
    if (unsaved) return;
    const created = await run(
      'design',
      () => setCaraWorkspaceArtifactDesign({ artifactId: artifact.id, designId }),
      (data) => data.setCaraWorkspaceArtifactDesign,
      "The design wasn't changed."
    );
    if (created) onVersionChange(created);
  };
  const markFinal = async () => {
    if (unsaved) return;
    const saved = await run(
      'mark',
      () =>
        markCaraWorkspaceArtifactFinal({
          artifactId: artifact.id,
          version: artifact.version
        }),
      (data) => data.markCaraWorkspaceArtifactFinal,
      "This version wasn't marked final."
    );
    if (saved) {
      setConfirmFinal(false);
      onArtifactSaved(saved);
    }
  };
  const unmarkFinal = async () => {
    const saved = await run(
      'unmark',
      () =>
        unmarkCaraWorkspaceArtifactFinal({ artifactId: artifact.id, version: artifact.version }),
      (data) => data.unmarkCaraWorkspaceArtifactFinal,
      'This version is still final.'
    );
    if (saved) onArtifactSaved(saved);
  };

  if (design.styleLinked && !final)
    return (
      <>
        {versionPicker()}
        {children}
      </>
    );
  // A version with no design is frozen with none, and the renderer reads its template as it
  // stands, so only its content is held.
  const rendersWithDesign = !!artifact.document?.resolvedDesign;
  const frozenNote = rendersWithDesign
    ? 'Its content and design are frozen.'
    : 'Its content is frozen.';
  const unmarkAction: VersionAction = {
    label: pending === 'unmark' ? 'Unmarking…' : 'Unmark final',
    disabled: busy,
    onSelect: () => void unmarkFinal()
  };
  const markAction: VersionAction = {
    label: pending === 'mark' ? 'Marking…' : 'Mark as final',
    disabled: busy || unsaved !== null,
    title:
      unsaved ??
      (rendersWithDesign
        ? "Freeze this version's content and design"
        : "Freeze this version's content"),
    onSelect: () => {
      // Only a version on a design freezes its template's page choices.
      if (design.templateLinked && rendersWithDesign) {
        setConfirmFinal(true);
      } else {
        void markFinal();
      }
    }
  };
  const finalAction = editable ? (final ? unmarkAction : markAction) : undefined;

  return (
    <>
      {versionPicker(finalAction)}
      {!design.styleLinked && choices && choices.designs.length > 0 && (
        <DesignPicker
          choices={choices}
          onOpen={onDesignsOpen}
          currentId={design.designId}
          ownLook={design.templateOwnLook}
          label={design.archived ? `${design.designName} (archived)` : design.designName}
          disabled={!editable || !latest || busy || unsaved !== null}
          hint={
            !editable
              ? 'The design this document prints with'
              : !latest
                ? 'Switch to the latest version to change its design'
                : (unsaved ??
                  (final
                    ? 'Makes a new version; this one stays final.'
                    : 'Change the design this document prints with'))
          }
          onChoose={(designId) => void choose(designId)}
        />
      )}
      {children}
      {final && (
        <Badge
          color="green"
          title={editable && latest ? `${frozenNote} Unmark final to edit it.` : frozenNote}
        >
          Final
        </Badge>
      )}
      <Dialog open={confirmFinal} onClose={() => !busy && setConfirmFinal(false)} size="md">
        <DialogTitle>Mark this version as final?</DialogTitle>
        <DialogDescription>
          Its content, template and page choices will be frozen for this version.
        </DialogDescription>
        <DialogBody>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            This keeps the template's current start and end page choices. Later edits to the
            template or design will not change this version.
          </p>
          {error && (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain disabled={busy} onClick={() => setConfirmFinal(false)}>
            Cancel
          </Button>
          <Button
            color="sky"
            loading={pending === 'mark'}
            disabled={busy || !!unsaved}
            onClick={() => void markFinal()}
          >
            Mark as final
          </Button>
        </DialogActions>
      </Dialog>
      {error && !confirmFinal && (
        <span role="alert" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </span>
      )}
    </>
  );
};

const DesignPicker = ({
  choices,
  currentId,
  ownLook,
  label,
  disabled,
  hint,
  onOpen,
  onChoose
}: {
  choices: AgencyDesignChoices;
  onOpen?: () => void;
  currentId: string | null;
  /** No design prints the document's Legacy template's own look, not the plain base. */
  ownLook: boolean;
  label: string | null;
  disabled: boolean;
  hint: string;
  /** Called with null for no design. */
  onChoose: (designId: string | null) => void;
}) => (
  <Dropdown>
    {/* Sized and labelled like the version picker beside it rather than a full Button. */}
    <DropdownButton
      as="button"
      disabled={disabled}
      title={hint}
      onClick={onOpen}
      className={DOCUMENT_PROVENANCE_CLASSES}
    >
      <SwatchIcon className="size-3.5 shrink-0" />
      <DocumentProvenanceLabel label="Design" name={label ?? (ownLook ? 'Legacy' : 'None')} />
      <ChevronUpDownIcon className="size-3.5 shrink-0" />
    </DropdownButton>
    {/* z-50 like the version picker's popover: the pages bar below is a sticky z-10. Opaque like
        it too, since the shared menu's translucent fill takes the color of the cover beneath. */}
    <DropdownMenu
      anchor="bottom start"
      className="z-50 max-h-[22rem] min-w-56 max-w-80 !bg-white dark:!bg-neutral-800"
    >
      <DropdownItem
        aria-current={currentId === null || undefined}
        onClick={() => {
          if (currentId !== null) onChoose(null);
        }}
      >
        <CheckIcon data-slot="icon" className={clsx(currentId !== null && 'invisible')} />
        <DropdownLabel>{ownLook ? 'Legacy' : 'None'}</DropdownLabel>
        {ownLook && <DropdownDescription>Template&apos;s own look</DropdownDescription>}
      </DropdownItem>
      {choices.designs.map((each) => {
        const current = each.id === currentId;
        return (
          <DropdownItem
            key={each.id}
            aria-current={current || undefined}
            onClick={() => {
              if (!current) onChoose(each.id);
            }}
          >
            <CheckIcon data-slot="icon" className={clsx(!current && 'invisible')} />
            <DropdownLabel className="truncate">{each.name}</DropdownLabel>
            {each.isDefault && <DropdownDescription>Default</DropdownDescription>}
          </DropdownItem>
        );
      })}
    </DropdownMenu>
  </Dropdown>
);
